/**
 * Dashboard Page
 * 
 * Main dashboard with balance evolution chart and account overview.
 */

import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useAccountStore } from '../store/accountStore';
import { useAuthStore } from '../store/authStore';
import { useUserSettingsStore, getDefaultAccountId } from '../store/userSettingsStore';
import { accountService, BalanceHistoryEntry } from '../services/account.service';
import { Card, Button } from '../components/ui';
import { formatCurrency } from '../utils/formatters';

interface ChartDataPoint {
  date: string;
  displayDate: string;
  balance: number;
  actualBalance?: number;
  projectedBalance?: number;
  isPast: boolean;
}

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isPremium } = useAuthStore();
  const { accounts, balances, fetchAccounts, isLoading } = useAccountStore();
  const { preferences } = useUserSettingsStore();
  
  // State for balance history chart
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // State for month navigation
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-indexed

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Select default account or first account when accounts are loaded
  useEffect(() => {
    if (!isLoading && accounts.length > 0 && !selectedAccountId) {
      const defaultAccountId = getDefaultAccountId();
      if (defaultAccountId && accounts.some(acc => acc.id === defaultAccountId)) {
        setSelectedAccountId(defaultAccountId);
      } else if (accounts[0]) {
        setSelectedAccountId(accounts[0].id);
      }
    }
  }, [isLoading, accounts, preferences, selectedAccountId]);

  // Fetch balance history when selected account or month changes
  useEffect(() => {
    if (selectedAccountId) {
      setIsLoadingHistory(true);
      accountService.getBalanceHistory(selectedAccountId, selectedYear, selectedMonth)
        .then(response => {
          if (response.success && response.data) {
            setBalanceHistory(response.data);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoadingHistory(false));
    }
  }, [selectedAccountId, selectedYear, selectedMonth]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Go to current month
  const goToCurrentMonth = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
  };

  // Check if we're on current month
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;

  // Format selected month name for display
  const selectedMonthName = new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric',
  });

  // Format chart data
  const chartData: ChartDataPoint[] = useMemo(() => {
    return balanceHistory.map(entry => {
      const dateObj = new Date(entry.date);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      return {
        date: entry.date,
        displayDate: `${day}/${month}`,
        balance: entry.balance,
        actualBalance: entry.actualBalance,
        projectedBalance: entry.projectedBalance,
        isPast: entry.isPast,
      };
    });
  }, [balanceHistory, i18n.language]);

  // Get selected account info
  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
  const selectedBalance = selectedAccountId ? balances[selectedAccountId] : null;

  // Calculate total balance across all accounts
  const totalBalance = Object.values(balances).reduce((sum, b) => sum + b.balance, 0);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; dataKey: string; name: string }>; label?: string }) => {
    if (active && payload && payload.length > 0 && selectedAccount) {
      const actualEntry = payload.find(p => p.name === 'actual');
      const projectedEntry = payload.find(p => p.name === 'projected');
      
      return (
        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{label}</p>
          {actualEntry && actualEntry.value !== undefined && actualEntry.value !== null && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-violet-500"></div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.actual', 'Réalisé')}:</span>
              <span className={`font-semibold ${actualEntry.value >= 0 ? 'text-slate-900 dark:text-slate-100' : 'text-red-600 dark:text-red-400'}`}>
                {formatCurrency(actualEntry.value, selectedAccount.currency)}
              </span>
            </div>
          )}
          {projectedEntry && projectedEntry.value !== undefined && projectedEntry.value !== null && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-violet-300"></div>
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.forecast', 'Prévisionnel')}:</span>
              <span className={`font-semibold ${projectedEntry.value >= 0 ? 'text-slate-700 dark:text-slate-300' : 'text-red-500 dark:text-red-400'}`}>
                {formatCurrency(projectedEntry.value, selectedAccount.currency)}
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 break-words">
          {t('dashboard.welcome', { email: user?.email })}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">{t('dashboard.subtitle')}</p>
      </div>

      {/* Total Balance Card */}
      <Card className="bg-gradient-to-r from-violet-600 to-violet-700">
        <div className="text-white">
          <p className="text-violet-200 text-sm">{t('dashboard.totalBalance')}</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(totalBalance, 'EUR')}</p>
          <p className="text-violet-200 text-sm mt-2">
            {accounts.length} {t('dashboard.accounts', { count: accounts.length })}
          </p>
        </div>
      </Card>

      {/* Balance Evolution Chart */}
      {accounts.length > 0 && (
        <Card noPadding className="overflow-hidden">
          {/* Header with title, navigation and selector - with padding */}
          <div className="px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('dashboard.balanceEvolution')}
                </h2>
                {/* Month navigation */}
                <div className="flex items-center gap-2 mt-1">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                    title={t('dashboard.previousMonth', 'Mois précédent')}
                  >
                    <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-slate-500 dark:text-slate-400 min-w-[140px] text-center capitalize">
                    {selectedMonthName}
                  </span>
                  <button
                    onClick={goToNextMonth}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                    title={t('dashboard.nextMonth', 'Mois suivant')}
                  >
                    <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {!isCurrentMonth && (
                    <button
                      onClick={goToCurrentMonth}
                      className="ml-2 px-2 py-1 text-xs text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded transition-colors"
                      title={t('dashboard.currentMonth', 'Mois actuel')}
                    >
                      {t('dashboard.today', 'Aujourd\'hui')}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Account selector for chart */}
              <select
                value={selectedAccountId || ''}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Current balance for selected account */}
            {selectedAccount && selectedBalance && (
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.currentBalance')}</p>
                  <p className={`text-xl sm:text-2xl font-bold ${selectedBalance.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(selectedBalance.balance, selectedAccount.currency)}
                  </p>
                </div>
                {selectedBalance.projectedBalance !== selectedBalance.balance && (
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.projectedBalance')}</p>
                    <p className={`text-lg sm:text-xl font-semibold ${selectedBalance.projectedBalance >= 0 ? 'text-slate-700 dark:text-slate-300' : 'text-red-500 dark:text-red-400'}`}>
                      {formatCurrency(selectedBalance.projectedBalance, selectedAccount.currency)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chart - full width on mobile */}
          {isLoadingHistory ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : chartData.length > 0 ? (
            <>
              <div className="h-64 -ml-2 sm:ml-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      {/* Gradient for actual/past balance (solid violet) */}
                      <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                      </linearGradient>
                      {/* Gradient for projected/future balance (lighter violet) */}
                      <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c4b5fd" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#c4b5fd" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    {/* Zero line - red reference */}
                    <ReferenceLine y={0} stroke="#ef4444" strokeWidth={2} strokeDasharray="4 4" />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                      tickFormatter={(value) => {
                        if (Math.abs(value) >= 1000) {
                          return `${(value / 1000).toFixed(0)}k`;
                        }
                        return value.toString();
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/* Projected balance area (entire month, lighter color) */}
                    <Area
                      type="monotone"
                      dataKey="projectedBalance"
                      stroke="#c4b5fd"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      fill="url(#projectedGradient)"
                      name="projected"
                    />
                    {/* Actual balance area (up to today, solid color) */}
                    <Area
                      type="monotone"
                      dataKey="actualBalance"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#actualGradient)"
                      name="actual"
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 pb-4 sm:pb-6 text-sm text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-0.5 bg-violet-500"></div>
                  <span>{t('dashboard.actualBalance', 'Réalisé')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-0.5 bg-violet-300 border-dashed border-t-2 border-violet-300"></div>
                  <span>{t('dashboard.forecastBalance', 'Prévisionnel')}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
              {t('dashboard.noDataForChart')}
            </div>
          )}
        </Card>
      )}

      {/* Accounts Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.yourAccounts')}</h2>
          <Link to="/accounts">
            <Button variant="ghost" size="sm">{t('common.viewAll')}</Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-slate-100 dark:bg-slate-700 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{t('dashboard.noAccounts.title')}</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">{t('dashboard.noAccounts.message')}</p>
              <Link to="/accounts/new">
                <Button>{t('dashboard.noAccounts.create')}</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => {
              const balance = balances[account.id];
              return (
                <Link key={account.id} to={`/accounts/${account.id}/operations`}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <div>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{account.name}</h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{account.bankName}</p>
                        </div>
                        <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className={`text-2xl font-bold ${balance && balance.balance >= 0 ? 'text-slate-900 dark:text-slate-100' : 'text-red-600 dark:text-red-400'}`}>
                          {balance ? formatCurrency(balance.balance, account.currency) : '—'}
                        </p>
                        {balance && balance.projectedBalance !== balance.balance && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {t('dashboard.projected')}: {formatCurrency(balance.projectedBalance, account.currency)}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Premium Upsell - hide for secondary users who inherit premium from owner */}
      {!isPremium() && !useAuthStore.getState().isSecondaryUser() && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-700">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t('dashboard.premium.title')}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('dashboard.premium.message')}</p>
            </div>
            <Link to="/premium">
              <Button variant="primary" size="sm">{t('dashboard.premium.upgrade')}</Button>
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
